import type { Contact, ContactPhoneNumber } from '@/types/contact';
import { normalizeContactPhoneToE164 } from './phone';

export type ContactPhonePayload = {
  primaryPhone?: string | null;
  altPhone?: string | null;
  phoneNumbers?: ContactPhoneNumber[];
};

export type FilledContactPhonePayload = {
  primaryPhone: string;
  phoneNumbers: ContactPhoneNumber[];
};

function collectStoredPhones(contact?: Contact): ContactPhoneNumber[] {
  const phones = [...(contact?.phoneNumbers ?? [])];

  if (contact?.altPhone) {
    phones.push({
      number: contact.altPhone,
      label: 'OTHER',
      isPrimary: false,
    });
  }

  return phones;
}

export function getContactPrimaryPhone(contact?: Contact | null) {
  const directPhone = normalizeContactPhoneToE164(contact?.primaryPhone ?? null);
  if (directPhone) return directPhone;

  const primaryEntry = contact?.phoneNumbers?.find((phone) => phone.isPrimary);
  return normalizeContactPhoneToE164(primaryEntry?.number ?? null);
}

export function getContactAlternatePhone(contact?: Contact | null) {
  const primaryPhone = getContactPrimaryPhone(contact);

  for (const phone of collectStoredPhones(contact ?? undefined)) {
    const normalizedPhone = normalizeContactPhoneToE164(phone.number);
    if (normalizedPhone && normalizedPhone !== primaryPhone) return normalizedPhone;
  }

  return null;
}

export function buildContactPhonePayload(
  contact: Contact | null | undefined,
  primaryPhone: string,
): FilledContactPhonePayload {
  const existingPhones = collectStoredPhones(contact ?? undefined);
  const currentPrimaryPhone = getContactPrimaryPhone(contact);
  const primaryEntry =
    existingPhones.find(
      (phone) => normalizeContactPhoneToE164(phone.number) === currentPrimaryPhone,
    ) ?? existingPhones.find((phone) => phone.isPrimary);
  const phoneNumbers: ContactPhoneNumber[] = [
    {
      number: primaryPhone,
      label: primaryEntry?.label ?? 'MOBILE',
      isPrimary: true,
    },
  ];
  const seen = new Set([primaryPhone]);

  for (const phone of existingPhones) {
    const normalizedPhone = normalizeContactPhoneToE164(phone.number);
    if (!normalizedPhone || seen.has(normalizedPhone)) continue;

    seen.add(normalizedPhone);
    phoneNumbers.push({
      number: normalizedPhone,
      label: phone.label ?? 'OTHER',
      isPrimary: false,
    });
  }

  return {
    primaryPhone,
    phoneNumbers,
  };
}

function buildClearAllPhonePayload(): ContactPhonePayload {
  return {
    primaryPhone: null,
    altPhone: null,
    phoneNumbers: [],
  };
}

function buildClearPrimaryPhonePayload(
  contact: Contact | undefined,
  storedPrimaryPhone: string,
): ContactPhonePayload {
  const remainingPhones: ContactPhoneNumber[] = [];
  const seen = new Set<string>();

  for (const phone of collectStoredPhones(contact)) {
    const normalizedPhone = normalizeContactPhoneToE164(phone.number);
    if (!normalizedPhone || normalizedPhone === storedPrimaryPhone || seen.has(normalizedPhone)) {
      continue;
    }

    seen.add(normalizedPhone);
    remainingPhones.push({
      number: normalizedPhone,
      label: phone.label ?? 'OTHER',
      isPrimary: false,
    });
  }

  const nextPrimary = remainingPhones[0];
  if (!nextPrimary) return buildClearAllPhonePayload();

  const phoneNumbers = remainingPhones.map((phone, index) => ({
    ...phone,
    isPrimary: index === 0,
  }));

  return {
    primaryPhone: nextPrimary.number,
    altPhone: phoneNumbers.find((phone) => !phone.isPrimary)?.number ?? null,
    phoneNumbers,
  };
}

export function buildContactEditPhonePayload(
  contact: Contact | null | undefined,
  rawPhone: string,
  normalizedPhone: string | null,
): ContactPhonePayload {
  const storedPrimaryPhone = getContactPrimaryPhone(contact);

  if (rawPhone) {
    if (!normalizedPhone || normalizedPhone === storedPrimaryPhone) return {};
    return buildContactPhonePayload(contact, normalizedPhone);
  }

  if (!storedPrimaryPhone) return {};
  return buildClearPrimaryPhonePayload(contact ?? undefined, storedPrimaryPhone);
}

function addPhoneNumber(
  phoneNumbers: ContactPhoneNumber[],
  seen: Set<string>,
  number: string | null,
  label: ContactPhoneNumber['label'] | undefined,
  isPrimary: boolean,
) {
  if (!number || seen.has(number)) return;

  seen.add(number);
  phoneNumbers.push({
    number,
    label: label ?? (isPrimary ? 'MOBILE' : 'OTHER'),
    isPrimary,
  });
}

function findStoredPhoneByNumber(
  phones: ContactPhoneNumber[],
  number: string | null,
): ContactPhoneNumber | undefined {
  if (!number) return undefined;
  return phones.find((phone) => normalizeContactPhoneToE164(phone.number) === number);
}

export function buildContactFormPhonePayload({
  contact,
  mode,
  primaryPhone,
  alternatePhone,
}: {
  contact?: Contact | null;
  mode: 'create' | 'edit';
  primaryPhone: string;
  alternatePhone: string;
}): ContactPhonePayload {
  const rawPrimaryPhone = primaryPhone.trim();
  const rawAlternatePhone = alternatePhone.trim();
  const normalizedPrimaryPhone = normalizeContactPhoneToE164(rawPrimaryPhone);
  const normalizedAlternatePhone = normalizeContactPhoneToE164(rawAlternatePhone);
  const nextPrimaryPhone = rawPrimaryPhone ? normalizedPrimaryPhone : null;
  const nextAlternatePhone =
    rawAlternatePhone && normalizedAlternatePhone !== nextPrimaryPhone
      ? normalizedAlternatePhone
      : null;

  const storedPrimaryPhone = getContactPrimaryPhone(contact);
  const storedAlternatePhone = getContactAlternatePhone(contact);

  if (
    mode === 'edit' &&
    nextPrimaryPhone === (storedPrimaryPhone ?? null) &&
    nextAlternatePhone === (storedAlternatePhone ?? null)
  ) {
    return {};
  }

  if (mode === 'create' && !nextPrimaryPhone && !nextAlternatePhone) {
    return {};
  }

  if (mode === 'edit' && !rawPrimaryPhone && !rawAlternatePhone) {
    return buildClearAllPhonePayload();
  }

  const existingPhones = collectStoredPhones(contact ?? undefined);
  const primaryEntry =
    findStoredPhoneByNumber(existingPhones, storedPrimaryPhone) ??
    existingPhones.find((phone) => phone.isPrimary);
  const alternateEntry = findStoredPhoneByNumber(existingPhones, storedAlternatePhone);
  const phoneNumbers: ContactPhoneNumber[] = [];
  const seen = new Set<string>();

  addPhoneNumber(phoneNumbers, seen, nextPrimaryPhone, primaryEntry?.label ?? 'MOBILE', true);
  addPhoneNumber(phoneNumbers, seen, nextAlternatePhone, alternateEntry?.label ?? 'OTHER', false);

  if (mode === 'edit') {
    for (const phone of existingPhones) {
      const normalizedPhone = normalizeContactPhoneToE164(phone.number);
      if (
        !normalizedPhone ||
        normalizedPhone === storedPrimaryPhone ||
        normalizedPhone === storedAlternatePhone
      ) {
        continue;
      }

      addPhoneNumber(phoneNumbers, seen, normalizedPhone, phone.label ?? 'OTHER', false);
    }
  }

  if (phoneNumbers.length > 0 && !phoneNumbers.some((phone) => phone.isPrimary)) {
    phoneNumbers[0] = { ...phoneNumbers[0], isPrimary: true };
  }

  const nextStoredPrimaryPhone = phoneNumbers.find((phone) => phone.isPrimary)?.number ?? null;
  const nextStoredAlternatePhone = phoneNumbers.find((phone) => !phone.isPrimary)?.number ?? null;

  if (!nextStoredPrimaryPhone) return buildClearAllPhonePayload();

  return {
    primaryPhone: nextStoredPrimaryPhone,
    altPhone: nextStoredAlternatePhone ?? (mode === 'edit' ? null : undefined),
    phoneNumbers,
  };
}
